/**
 * What is on screen in a manual list's board, lane by lane, and what a read or a write
 * does to it. Pure, so the rules that keep counts honest are tested without React.
 *
 * `total` is the server's count for the lane, adjusted by what the user has done since.
 * It is never `items.length`: a lane loads in pages.
 */

/** The lane for members whose stage is none of the list's stages. */
export const NO_STAGE = `__no_stage__`

export type LaneItem = { id?: string | null }
export type LaneState<T> = {
  items: T[]
  total: number
  hasMore: boolean
  page: number
  /**
   * Cards on screen that no read has returned yet, because the search index lags a write.
   * They are in `items` and counted in `total`, on top of the server's count.
   */
  pendingIds: string[]
}
export type LanesState<T> = Record<string, LaneState<T>>

const EMPTY_LANE = { items: [], total: 0, hasMore: false, page: 0, pendingIds: [] }

/** Cards just added that a read may not return yet, because the search index lags a write. */
export function stillPending<T extends LaneItem>(pending: T[], hits: T[]): T[] {
  const returned = new Set(hits.map((hit) => hit.id))
  return pending.filter((candidate) => !returned.has(candidate.id))
}

export function withPage<T extends LaneItem>(
  state: LanesState<T>,
  laneKey: string,
  page: number,
  hits: T[],
  total: number,
  pageSize: number,
  pending: T[],
): LanesState<T> {
  const hasMore = (page + 1) * pageSize < total
  if (page === 0) {
    const waiting = stillPending(pending, hits)
    const waitingIds = waiting.flatMap((existing) => (existing.id ? [existing.id] : []))
    const seen = new Set(waiting.map((existing) => existing.id))
    const fresh = hits.filter((hit) => !seen.has(hit.id))
    return {
      ...state,
      [laneKey]: {
        items: [...waiting, ...fresh],
        total: total + waiting.length,
        hasMore,
        page,
        pendingIds: waitingIds,
      },
    }
  }
  const loaded = state[laneKey]?.items ?? []
  // A later page never trusts the caller's `pending` list: it is never pruned, so a card the
  // server already returned would be counted twice. Only this lane's own record of who is
  // still waiting, minus whoever this page's hits just confirmed, survives.
  const hitIds = new Set(hits.map((hit) => hit.id))
  const pendingIds = (state[laneKey]?.pendingIds ?? []).filter((id) => !hitIds.has(id))
  const seen = new Set(loaded.map((existing) => existing.id))
  const fresh = hits.filter((hit) => !seen.has(hit.id))
  return {
    ...state,
    [laneKey]: { items: [...loaded, ...fresh], total: total + pendingIds.length, hasMore, page, pendingIds },
  }
}

export function laneOf<T extends LaneItem>(state: LanesState<T>, itemId: string): string | undefined {
  return Object.keys(state).find((laneKey) => state[laneKey].items.some((existing) => existing.id === itemId))
}

export function withRemoved<T extends LaneItem>(state: LanesState<T>, itemId: string): LanesState<T> {
  const laneKey = laneOf(state, itemId)
  if (!laneKey) return state
  const lane = state[laneKey]
  return {
    ...state,
    [laneKey]: {
      ...lane,
      items: lane.items.filter((existing) => existing.id !== itemId),
      total: Math.max(0, lane.total - 1),
      pendingIds: lane.pendingIds.filter((id) => id !== itemId),
    },
  }
}

export function withAdded<T extends LaneItem>(state: LanesState<T>, laneKey: string, added: T[]): LanesState<T> {
  const lane = state[laneKey] ?? EMPTY_LANE
  const shown = new Set(lane.items.map((existing) => existing.id))
  const fresh = added.filter((candidate) => !shown.has(candidate.id))
  // A card just added is not in the server's count for this lane yet, so it is waiting too.
  const freshIds = fresh.flatMap((candidate) => (candidate.id ? [candidate.id] : []))
  return {
    ...state,
    [laneKey]: {
      ...lane,
      items: [...fresh, ...lane.items],
      total: lane.total + fresh.length,
      pendingIds: [...lane.pendingIds, ...freshIds],
    },
  }
}

export function withMove<T extends LaneItem>(
  state: LanesState<T>,
  itemId: string,
  fromKey: string | undefined,
  toKey: string,
  moved: T,
): LanesState<T> {
  if (fromKey === toKey) return state
  return withAdded(withRemoved(state, itemId), toKey, [moved])
}
